import { notionSearch } from "../services/notion";
import { getPageTitle } from "../helper/canvasUtils";
import { notionResultType } from "../helper/notionResult";
import { NotionRawPage } from "../types";

/**
 * The workspace's Notion pages, fetched ONCE and held for the session.
 *
 * The picker used to search Notion on every keystroke: nothing to look at until
 * you had typed three characters, a round trip per keystroke after that, and no
 * way to see what you actually have access to. The list is small enough to hold
 * — a few hundred rows of id/title/icon — so it is fetched once and filtered in
 * memory, which is both instant and free.
 *
 * The cache is module-scoped and keyed by nothing, so it MUST be dropped on
 * sign-out: the next account would otherwise be shown the previous one's page
 * list (see `useAuth`).
 */

export type NotionPageListItem = {
  id?: string;
  title?: string;
  type?: string;
  icon?: {
    type: string;
    [key: string]: string;
  };
  publicUrl: string | null;
};

export const mapNotionPageResult = (
  page: NotionRawPage,
): NotionPageListItem => ({
  id: page.id,
  title: getPageTitle(page),
  // Modern Notion `search` returns a database as `object: "data_source"`; fold
  // it into the "database" kind the picker classifies on.
  type: notionResultType(page.object),
  icon: page.icon,
  publicUrl: page.public_url,
});

/**
 * The ceiling the edge function paginates up to (`MAX_SEARCH_PAGES * 100`).
 *
 * Documentation only — truncation is REPORTED by the function via `has_more`,
 * never inferred here from a row count, so a workspace of exactly the ceiling
 * is correctly treated as complete.
 */
export const NOTION_PAGES_FETCH_CAP = 500;

export type NotionPagesSnapshot = {
  pages: readonly NotionPageListItem[];
  status: "idle" | "loading" | "loaded" | "failed";
  /**
   * Notion had more to give than the fetch returned, so the in-memory list
   * cannot answer a query on its own.
   */
  partial: boolean;
};

const EMPTY: NotionPagesSnapshot = {
  pages: [],
  status: "idle",
  partial: false,
};

let snapshot: NotionPagesSnapshot = EMPTY;
let inFlight: Promise<void> | null = null;

/**
 * Bumped by every invalidation. A load captures it at the start and writes
 * nothing if it no longer matches — a sign-out lands DURING the load far more
 * often than not (it is five sequential Notion calls, and `signOut` invalidates
 * synchronously), and without this the signed-out account's page list is
 * written straight back into the cache the invalidation just cleared.
 *
 * It guards the `inFlight` bookkeeping too: a superseded load must not null out
 * the handle its replacement is holding, or a third caller starts a third
 * concurrent request.
 */
let generation = 0;

const listeners = new Set<() => void>();

/**
 * A NEW object on every change, and the same object between changes: consumers
 * hold it in state, so a fresh identity is what makes React re-render and a
 * stable one is what stops it re-rendering forever.
 */
const setSnapshot = (next: Partial<NotionPagesSnapshot>): void => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
};

export const subscribeNotionPagesCache = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getNotionPagesSnapshot = (): NotionPagesSnapshot => snapshot;

/**
 * `{ results, has_more }` from a `fetch_all` call, or the bare array older
 * deployments of the edge function still return — the client and the function
 * ship on separate pipelines, so both shapes are live during a rollout.
 *
 * A bare array is ALWAYS treated as partial. The old function never saw
 * `fetch_all` (the Notion SDK silently drops parameters it does not know, it
 * does not reject them), so it answered with one un-paginated page of 100 —
 * and inferring completeness from the row count would mark those 100 as the
 * whole workspace and suppress the fallback search, making everything past
 * page 1 unreachable for the length of the rollout.
 */
const parseResponse = (
  data: unknown,
): { results: NotionRawPage[]; hasMore: boolean } | null => {
  if (Array.isArray(data)) {
    return { results: data as NotionRawPage[], hasMore: true };
  }
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as any).results)
  ) {
    const body = data as { results: NotionRawPage[]; has_more?: boolean };
    return { results: body.results, hasMore: body.has_more === true };
  }
  return null;
};

/**
 * Load the workspace list, at most once. Safe to call from anywhere — sign-in,
 * and again when the picker opens — because concurrent callers share the one
 * in-flight request and a completed load is a no-op.
 *
 * A FAILED load is not remembered: the next caller retries, which is what makes
 * re-opening the picker a working recovery from an offline moment.
 */
export const prefetchNotionPages = (): Promise<void> => {
  if (snapshot.status === "loaded") {
    return Promise.resolve();
  }
  if (inFlight) {
    return inFlight;
  }

  const myGeneration = generation;
  setSnapshot({ status: "loading" });
  inFlight = (async () => {
    try {
      const { data, error } = await notionSearch({
        page_size: 100,
        // Most-recently-edited first: with no query typed this list IS the
        // picker's default, and "the pages I touched today" is the only order
        // that makes the top of it worth reading.
        sort: { direction: "descending", timestamp: "last_edited_time" },
        // Read by the edge function and stripped before the call to Notion:
        // follow `has_more` instead of returning only the first 100.
        fetch_all: true,
      });
      if (generation !== myGeneration) {
        return;
      }
      const parsed = parseResponse(data);
      if (error || !parsed) {
        setSnapshot({ status: "failed" });
        return;
      }
      setSnapshot({
        pages: parsed.results.map(mapNotionPageResult),
        status: "loaded",
        partial: parsed.hasMore,
      });
    } catch {
      if (generation === myGeneration) {
        setSnapshot({ status: "failed" });
      }
    } finally {
      if (generation === myGeneration) {
        inFlight = null;
      }
    }
  })();

  return inFlight;
};

/** Forget the list entirely (sign-out, account switch). */
export const invalidateNotionPagesCache = (): void => {
  // Before clearing, so a load already in flight for the OLD account can no
  // longer write its results back in.
  generation += 1;
  inFlight = null;
  snapshot = EMPTY;
  listeners.forEach((listener) => listener());
};

/** Test-only: reset module state between cases. */
export const resetNotionPagesCacheForTests = (): void => {
  invalidateNotionPagesCache();
};
